import { Button, PageLoader } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { useIntegrationsWorkspaceId } from '../../workspace-id';
import { createPopularIntegrationsList, PopularIntegrations, type PopularIntegration } from '../../catalog/images';
import { useProductTaxonomyTerms } from '../../directory/taxonomy';
import { useConnectionsViewModel } from '../hooks';
import { ConnectionsBlankSlate } from './connections-blank-slate';
import { ConnectionsTable } from './connections-table';

// The migrated URL, not the `journeys` one: this is an in-Hydra link, so it should not pay
// for the legacy handoff redirect.
const DIRECTORY_PATH = '/workspaces/$workspaceId/integrations/all/directory';

/** The 15 popular connections the empty state promotes (ported from the Ember overview controller). */
const POPULAR_OVERVIEW_INTEGRATIONS: PopularIntegration[] = [
  PopularIntegrations.salesforce,
  PopularIntegrations.bigquery,
  PopularIntegrations.redshift,
  PopularIntegrations.snowflake,
  PopularIntegrations.postgres,
  PopularIntegrations.mysql,
  PopularIntegrations.javascript,
  PopularIntegrations.ios,
  PopularIntegrations.android,
  PopularIntegrations.node,
  PopularIntegrations.go,
  PopularIntegrations.jotform,
  PopularIntegrations.typeform,
  PopularIntegrations.facebookLeadAds,
  PopularIntegrations.http,
];

/**
 * The Connections tab (the `/integrations/all/overview` route body). Every data
 * source is a non-suspense query that degrades to its absent default on error,
 * so a failing source (e.g. a 500 from the pipelines or catalog endpoint)
 * renders the page with whatever resolved rather than throwing to the app error
 * boundary — matching the Ember overview. `isLoading` gates the page loader
 * until every source has settled.
 */
export function ConnectionsTab() {
  const router = useRouter();
  const workspaceId = String(useIntegrationsWorkspaceId());
  const terms = useProductTaxonomyTerms();
  const { connections, pipelinesConnectionsById, isLoading, shouldGroupAutoTrack } = useConnectionsViewModel();

  if (isLoading) {
    return <PageLoader />;
  }

  const directoryHref = router.buildLocation({ to: DIRECTORY_PATH, params: { workspaceId } }).href;

  if (connections.length === 0) {
    const popularIntegrations = createPopularIntegrationsList(workspaceId, POPULAR_OVERVIEW_INTEGRATIONS);
    return (
      <ConnectionsBlankSlate
        icon="database"
        title={`Bring in your ${terms.customerSingular} data to start sending messages and boosting engagement.`}
        description="Connect the tools your team already uses or integrate directly with your app through our API."
        popularIntegrationsText="Get started with one of our most popular connections"
        popularIntegrations={popularIntegrations}
        workspaceId={workspaceId}
      >
        <Button href={directoryHref} variant="primary">
          View Integrations
        </Button>
      </ConnectionsBlankSlate>
    );
  }

  const addDataInHref = router.buildLocation({
    to: DIRECTORY_PATH,
    params: { workspaceId },
    search: { selectedType: 'Data in' },
  }).href;
  const addDataOutHref = router.buildLocation({
    to: DIRECTORY_PATH,
    params: { workspaceId },
    search: { selectedType: 'Data out' },
  }).href;

  return (
    <ConnectionsTable
      connections={connections}
      isSearchable
      addConnectionButtonHref={directoryHref}
      addDataInConnectionButtonHref={addDataInHref}
      addDataOutConnectionButtonHref={addDataOutHref}
      shouldGroupAutoTrack={shouldGroupAutoTrack}
      pipelinesConnectionsById={pipelinesConnectionsById}
      workspaceId={workspaceId}
    />
  );
}
